/**
 * Redux Slice: venuesSlice
 * @description Manages the state for venue data, including fetching, creating, updating, deleting, and selecting venues. Each reducer action updates the state to reflect the current status of venue data and the selected venue.
 *
 * @initialState
 *   @property {Array} venues - Stores the list of venues.
 *   @property {Array} businessHours - Stores the business hours data for the selected venue, with day-by-day operating hours including public hours, split hours, and workday hours.
 *   @property {string} status - Tracks the fetch status; can be 'idle', 'loading', 'succeeded', or 'failed'.
 *   @property {string} businessHoursStatus - Tracks the business hours fetch status; can be 'idle', 'loading', 'succeeded', or 'failed'.
 *   @property {string|null} error - Holds any error messages if fetching fails.
 *   @property {string|null} businessHoursError - Holds any error messages if business hours fetching fails.
 *   @property {string|null} selectedVenueId - Stores the ID of the currently selected venue.
 *
 * @reducers
 *   @function fetchVenuesStart - Sets the status to 'loading' when venue fetch begins.
 *   @function fetchVenuesSuccess - Sets the status to 'succeeded' and updates venues with fetched data.
 *   @function fetchVenuesFailure - Sets the status to 'failed' and stores the error message.
 *   @function selectVenue - Stores the selected venue ID in the state.
 *   @function setVenues - Directly sets the venues array
 *
 * @extraReducers
 *   Handles async thunk actions for fetchVenues
 *
 * @selectors
 *   @function selectAllVenues - Selects and returns all venues from the Redux state.
 *   @function selectStatus - Returns the current API status.
 *   @function selectVenuesAndStatus - Returns venues and status (memoized).
 *   @function selectSelectedVenue - Returns the currently selected venue.
 *
 * @example
 * import { selectVenue, selectAllVenues } from './venuesSlice';
 * import { fetchVenues } from './venuesThunks';
 */

import { createSlice, createSelector } from '@reduxjs/toolkit';
import { fetchVenues, fetchBusinessHours } from './venuesThunks';

const initialState = {
  venues: [],
  businessHours: [],
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  businessHoursStatus: 'idle',
  error: null,
  businessHoursError: null,
  selectedVenueId: null,
};

const venuesSlice = createSlice({
  name: 'venues',
  initialState,
  reducers: {
    fetchVenuesStart(state) {
      state.status = 'loading';
    },
    fetchVenuesSuccess(state, action) {
      state.status = 'succeeded';
      state.venues = action.payload;
    },
    fetchVenuesFailure(state, action) {
      state.status = 'failed';
      state.error = action.payload;
    },
    selectVenue(state, action) {
      state.selectedVenueId = action.payload;
    },
    setVenues: (state, action) => {
      state.venues = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVenues.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchVenues.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.venues = action.payload;
      })
      .addCase(fetchVenues.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(fetchBusinessHours.pending, (state) => {
        state.businessHoursStatus = 'loading';
      })
      .addCase(fetchBusinessHours.fulfilled, (state, action) => {
        state.businessHoursStatus = 'succeeded';
        state.businessHours = action.payload;
      })
      .addCase(fetchBusinessHours.rejected, (state, action) => {
        state.businessHoursStatus = 'failed';
        state.businessHoursError = action.payload;
      });
  },
});

export const { fetchVenuesStart, fetchVenuesSuccess, fetchVenuesFailure, selectVenue, setVenues } = venuesSlice.actions;

export default venuesSlice.reducer;

// Basic Selectors
export const selectAllVenues = (state) => state.venues.venues;
export const selectStatus = (state) => state.venues.status;
export const selectBusinessHours = (state) => state.venues.businessHours;
export const selectBusinessHoursStatus = (state) => state.venues.businessHoursStatus;

// Memoized Selector
export const selectVenuesAndStatus = createSelector([selectAllVenues, selectStatus], (venues, status) => ({
  venues,
  status,
}));

export const selectBusinessHoursAndStatus = createSelector(
  [selectBusinessHours, selectBusinessHoursStatus],
  (businessHours, status) => ({
    businessHours,
    status,
  })
);
